/* eslint-disable @typescript-eslint/no-unused-vars */
import { gql } from '@apollo/client';

export const StudentsQuery = gql`
  query PaginatedStudents($query: String, $first: Int, $after: String) {
    allStudents(query: $query, first: $first, after: $after) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          id
          isLmsBlocked
          user {
            id
            lastName
            firstName
            middleName
            phone
            email
          }
          agreements {
            id
            agreementTitle
            currency
            budgetAmount
            paidAmount
            debtAmount
            upcomingAmount
            terminated
            group {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const loadAgreementData = gql`
  query loadAgreementById($agreementId: ID!) {
    agreement(agreementId: $agreementId) {
      id
      agreementTitle
      currency
      terminated
      paymentByCompany
      vat
      date
      comment
      budgetAmount
      paidAmount
      debtAmount
      upcomingAmount
      partialAttendance
      terminatedDate
      transferOut
      freeOfCharge
      statePrefinanced
      firstLesson {
        id
        name
        lessonNumber
      }
      lastLesson {
        id
        name
        date
        lessonNumber
      }
      chargeback {
        id
        amountByn
        amountForeign
        currency
      }
      contributions(orderByDateFilter: true) {
        edges {
          node {
            id
            date
            amountForeign
            amountByn
          }
        }
      }
      promocode
      company {
        id
        name
      }
      country {
        id
        title
      }
      resident
      student {
        id
        user {
          id
          firstName
          middleName
          lastName
          phone
          email
        }
      }
      group {
        id
        name
        startDate
        course {
          id
          name
        }
      }
      startAccountsPayable {
        id
        amountByn
        amountForeign
        repaid
      }
      accountsPayable {
        id
        amountForeign
        currency
        startedAgreement {
          id
          agreementTitle
        }
      }
    }
  }
`;

export const MetaStudentsQuery = gql`
  query MetaStudents($query: String) {
    metaStudents(query: $query) {
      totalCount
      agreementsCount
    }
  }
`;

export const UserExistCheck = gql`
  query CheckUserExist($email: String!, $phone: String!) {
    verifyStudent(email: $email, phone: $phone) {
      student {
        id
        user {
          id
          firstName
          lastName
          middleName
          phone
          email
          roles
        }
      }
      user {
        id
        firstName
        lastName
        middleName
        phone
        email
        roles
      }
      customErrors {
        field
        message
        code
      }
      message
      code
    }
  }
`;

export const loadLessonsOptionsForGroup = gql`
  query loadLessonsOptions($groupId: ID!) {
    group(groupId: $groupId) {
      id
      lessons {
        id
        name
        date
        lessonNumber
      }
    }
  }
`;

export const loadAccountsPayables = gql`
  query accountsPayables($id: ID!) {
    student(id: $id) {
      id
      accountsPayables {
        id
        amountForeign
        currency
        repaid
        startedAgreement {
          id
          agreementTitle
        }
      }
    }
  }
`;

/** Stored LMS-access block for a student (null if never blocked).
 *  ТЗ 0074: + who/when/source and the invoices the suspension is tied to. */
export const StudentLmsBlockQuery = gql`
  query StudentLmsBlock($studentId: ID!) {
    studentAccessBlock(studentId: $studentId) {
      id
      isBlocked
      reason
      overdueInvoiceCount
      maxOverdueDays
      source
      blockedAt
      blockedByName
      unblockedAt
      unblockedByName
      blockingInvoices {
        invoiceId
        fakturowniaId
        number
        amount
        gross
        currency
        paymentTo
        daysOverdue
        paymentUrl
        viewUrl
        isPaid
      }
      amountsDue {
        amount
        currency
      }
    }
  }
`;

/** ТЗ 0074: a student's suspensions / restorations, newest first.
 *  Operation name ≠ backend field name (codegen result-type collision). */
export const StudentLmsBlockHistoryQuery = gql`
  query StudentLmsBlockHistory($studentId: ID!) {
    studentAccessBlockEvents(studentId: $studentId) {
      id
      action
      source
      actorName
      createdAt
      reason
      notifyStudent
      invoices {
        invoiceId
        number
        amount
        currency
        paymentTo
        daysOverdue
        viewUrl
      }
      amountsDue {
        amount
        currency
      }
      notifications {
        id
        channel
        kind
        status
        skipReason
        error
        sentAt
        createdAt
      }
    }
  }
`;

/** ТЗ 0074 «Wstrzymany dostęp» tab on /debtors: one server page. */
export const LmsSuspensionsPageQuery = gql`
  query LmsSuspensionsPage(
    $status: String
    $search: String
    $offset: Int
    $limit: Int
  ) {
    lmsSuspensions(
      status: $status
      search: $search
      offset: $offset
      limit: $limit
    ) {
      total
      items {
        status
        studentId
        studentName
        studentEmail
        groups {
          id
          name
        }
        source
        reason
        blockedAt
        blockedByName
        restoredAt
        restoredByName
        restoredSource
        skipReason
        invoices {
          invoiceId
          fakturowniaId
          number
          amount
          gross
          currency
          paymentTo
          daysOverdue
          paymentUrl
          viewUrl
          isPaid
        }
        amountsDue {
          amount
          currency
        }
        notifications {
          id
          channel
          kind
          status
          skipReason
          error
          sentAt
          createdAt
        }
      }
    }
  }
`;

/** ТЗ 0074: the suspension robot's latest run (null — never ran). */
export const LmsBlockRobotLatestRunQuery = gql`
  query LmsBlockRobotLatestRun {
    lmsBlockRobotLastRun {
      id
      taskName
      status
      triggerSource
      startedAt
      finishedAt
      stats
      errorMessage
    }
  }
`;

/** Debtor candidates — students with an issued invoice overdue > threshold.
 *  Operation name ≠ backend field name (codegen result-type collision). */
export const DebtorCandidatesQuery = gql`
  query DebtorCandidatesList($groupId: ID) {
    debtorCandidates(groupId: $groupId) {
      student {
        id
        user {
          id
          firstName
          lastName
          middleName
          email
        }
      }
      # Просрочка по валютам (PLN первой): одно число складывало валюты.
      overdueAmounts {
        amount
        currency
      }
      overdueInvoiceCount
      maxOverdueDays
      currentlyBlocked
    }
  }
`;

// --- Notes (spec 0003 Phase 2) ---
export const StudentNotesQuery = gql`
  query StudentNotesList($studentId: ID!) {
    studentNotes(studentId: $studentId) {
      id
      body
      sentiment
      pinned
      createdAt
      authorName
    }
  }
`;

export const GroupNotesQuery = gql`
  query GroupNotesList($groupId: ID!) {
    groupNotes(groupId: $groupId) {
      id
      body
      sentiment
      pinned
      createdAt
      authorName
    }
  }
`;

// --- Tasks (spec 0003 Phase 2 + redesign: student / agreement / group) ---
export const StudentTasksQuery = gql`
  query StudentTasksList($studentId: ID!, $includeClosed: Boolean) {
    studentTasks(studentId: $studentId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
      }
    }
  }
`;

export const GroupTasksQuery = gql`
  query GroupTasksList($groupId: ID!, $includeClosed: Boolean) {
    groupTasks(groupId: $groupId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
      }
    }
  }
`;

export const AgreementTasksQuery = gql`
  query AgreementTasksList($agreementId: ID!, $includeClosed: Boolean) {
    agreementTasks(agreementId: $agreementId, includeClosed: $includeClosed) {
      id
      title
      description
      status
      priority
      dueDate
      completedAt
      createdAt
      assigneeName
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
      }
    }
  }
`;

export const MyTasksQuery = gql`
  query MyTasksList($includeClosed: Boolean) {
    myTasks(includeClosed: $includeClosed) {
      id
      title
      status
      priority
      dueDate
      assigneeName
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
      }
    }
  }
`;

// Счётчики шапки командного центра (ТЗ 0063). Отдельный запрос, а не поле
// внутри ленты: шапка живёт выше `TaskBoard`, ей нужны числа по всей выборке,
// а не по загруженной странице, и обновляется она своим интервалом.
//
// Скоуп счётчиков — РЕЖИМ и ГРУППА, и только они. Глубокие фильтры (статусы,
// приоритеты, связи, поиск) сюда НЕ идут, и это осознанно: клик по метрике сам
// ставит фильтр срочности, поэтому метрика, зависящая от фильтров, считала бы
// саму себя, а число дёргалось бы на каждую букву в поиске (см. докблок
// `useTaskSummary`).
//
// ⚠️ Здесь стояло обратное требование — «держать аргументы в ТОЧНОМ
// соответствии со `StaffTasksList`». Оно спорило с реальностью и с соседним
// файлом; следующий, кто взялся бы «починить расхождение», сломал бы метрики.
export const StaffTasksSummaryQuery = gql`
  query StaffTasksSummary(
    $groupId: ID
    $assigneeMe: Boolean
    $assigneeIds: [ID]
  ) {
    staffTasksSummary(
      groupId: $groupId
      assigneeMe: $assigneeMe
      assigneeIds: $assigneeIds
    ) {
      openCount
      overdueCount
      todayCount
    }
  }
`;

// Flexible task feed for the command center (filter by subject / assignee=me).
export const StaffTasksQuery = gql`
  query StaffTasksList(
    $groupId: ID
    $studentId: ID
    $agreementId: ID
    $teacherId: ID
    $assigneeMe: Boolean
    $assigneeIds: [ID]
    $statuses: [String]
    $priorities: [String]
    $dueFrom: Date
    $dueTo: Date
    $search: String
    $includeClosed: Boolean
    $closedWithinDays: Int
    $links: [TaskLinkInput]
  ) {
    staffTasks(
      groupId: $groupId
      studentId: $studentId
      agreementId: $agreementId
      teacherId: $teacherId
      assigneeMe: $assigneeMe
      assigneeIds: $assigneeIds
      statuses: $statuses
      priorities: $priorities
      dueFrom: $dueFrom
      dueTo: $dueTo
      search: $search
      includeClosed: $includeClosed
      closedWithinDays: $closedWithinDays
      links: $links
    ) {
      id
      title
      description
      status
      priority
      startDate
      dueDate
      position
      completedAt
      createdAt
      updatedAt
      assigneeId
      assigneeName
      createdByName
      commentsCount
      # Только СЧЁТЧИК файлов: сами строки нужны в окне, а в ленте это были бы
      # метаданные всех файлов по всем задачам. Ссылок здесь нет в принципе.
      attachmentsCount
      # ТЗ 0109: первая картинка самой задачи — обложка карточки доски. Ссылку
      # на неё даёт staffTaskImageUrls пачкой по видимым карточкам.
      coverAttachmentId
      # ТЗ 0129: задача, связанная с Kommo, — значок «Kommo»; kommoUrl — ссылка
      # на сделку (null у подделки и у того, кому лиды закрыты).
      kommoTaskId
      kommoUrl
      # ТЗ 0064: предмет задачи — плоский список связей. Раньше здесь были
      # четыре вложенных объекта; выбирать поля у StudentType/TeacherFilterType
      # ради имени было и дорого, и опасно (роле-гейтнутые поля рядом).
      # ТЗ 0088: href — готовый адрес связи, у которой он есть (сигнал
      # маркетинга — раздел с фильтром сигнала); у прочих видов и у того, кому
      # раздел закрыт, — null.
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
      }
    }
  }
`;

// Виды связей, доступные ЭТОЙ роли (ТЗ 0064). Список приезжает с сервера, а
// не хардкодится: право вести задачи шире права искать преподавателей или
// дофинансирование, и предлагать вид, поиск по которому вернёт пусто, — хуже,
// чем не предлагать его вовсе.
export const TaskLinkKindsQuery = gql`
  query TaskLinkKindsList {
    taskLinkKinds {
      key
      label
    }
  }
`;

// Подсказки пикера связей: ОДИН запрос на все двенадцать видов. Двенадцать
// разных запросов означали бы двенадцать разных форм ответа на фронте.
export const TaskLinkSearchQuery = gql`
  query TaskLinkSearchList($kind: String!, $query: String, $limit: Int) {
    taskLinkSearch(kind: $kind, query: $query, limit: $limit) {
      objectId
      name
      hint
    }
  }
`;

// Карточка задачи: то же, что в ленте, плюс тела реплик. В ленте их не
// выбирают — это были бы тексты всех реплик по всем задачам.
export const TaskDetailsQuery = gql`
  query TaskDetailsCard($id: ID!) {
    staffTask(id: $id) {
      id
      comments {
        id
        body
        authorName
        createdAt
        editedAt
        # Право называет СЕРВЕР: сравнивать id автора на клиенте — вторая
        # копия правила, и она уже разошлась (id пользователя тут relay-строка).
        canEdit
        attachments {
          id
          originalName
          contentType
          sizeBytes
          isImage
          commentId
          uploadedByName
          createdAt
          canDelete
        }
      }
      links {
        id
        kind
        objectId
        name
        hint
        missing
        href
        createdByName
        createdAt
      }
      # Файлы задачи и её реплик (ТЗ 0066). Ссылки здесь НЕТ: её минтит
      # отдельный запрос по клику — подписанная ссылка это bearer-токен, и в
      # кеше Apollo ей не место.
      attachmentsCount
      attachments {
        id
        originalName
        contentType
        sizeBytes
        isImage
        commentId
        uploadedByName
        createdAt
        canDelete
      }
    }
  }
`;

// Ссылка на файл — ОТДЕЛЬНЫМ запросом и по клику. Живёт 5 минут, поэтому
// читается только `network-only` и никогда не кладётся в кеш.
export const TaskAttachmentUrlQuery = gql`
  query TaskAttachmentLink($attachmentId: ID!) {
    staffTaskAttachmentUrl(attachmentId: $attachmentId) {
      url
      expiresAt
      originalName
    }
  }
`;

// Что сервер принимает: размер, количество, белый список. Клиент их НЕ
// повторяет — вторая копия правила разошлась бы с первой.
export const TaskAttachmentLimitsQuery = gql`
  query TaskAttachmentRules {
    taskAttachmentLimits {
      maxBytes
      maxPerTask
      allowedExtensions
      acceptAttribute
    }
  }
`;

// Active staff (management roles) for assignee pickers. Operation name ≠
// backend field (`staffUsers`) to avoid codegen result-type collision.
export const StaffUsersQuery = gql`
  query StaffUsersList {
    staffUsers {
      id
      name
      email
    }
  }
`;

// Aggregated student mini-card for the communicator hover-card (spec 0009).
// `finance` and the per-group financing flags come back null for viewers
// without payment roles — render them only on === true / non-null.
export const StudentMiniCardQuery = gql`
  query StudentMiniCardView($studentId: ID!) {
    studentMiniCard(studentId: $studentId) {
      studentId
      firstName
      lastName
      middleName
      email
      phone
      isLmsBlocked
      groups {
        agreementId
        agreementTitle
        groupId
        groupName
        courseName
        status
        terminatedDate
        freeOfCharge
        statePrefinanced
        paymentByCompany
      }
      finance {
        isDebtor
        # Просрочка по валютам (PLN первой): одно число складывало валюты.
        overdueAmounts {
          amount
          currency
        }
        overdueInvoiceCount
        maxOverdueDays
        overdueSince
        isBlocked
        blockReason
        totals {
          byCurrency {
            currency
            totalBudget
            totalPaid
            totalDebt
            totalUpcoming
          }
        }
      }
      openTasksCount
      recentTasks {
        id
        title
        status
        priority
        dueDate
        assigneeName
      }
      notesCount
      recentNotes {
        id
        body
        sentiment
        pinned
        createdAt
        authorName
      }
      # Spec 0038. Null (not empty) for roles without survey access — the
      # block quotes the student verbatim and names the teacher, unlike the
      # counts around it.
      surveys {
        respondedCount
        pendingCount
        avgScore
        negativeCount
        lastSubmittedAt
        recentSignals {
          responseId
          surveyTitle
          groupName
          lessonNumber
          teacherName
          submittedAt
          score
          sentiment
          excerpt
        }
      }
    }
  }
`;

/**
 * #54 — Slack-workspace групп договоров студента (и расторгнутых): ссылки
 * «открыть Slack группы», «профиль студента», «админка участников» для ручной
 * деактивации. Ссылки собирает сервер.
 *
 * Поле читает только управленец ERP (MANAGEMENT_ROLES; остальным сервер
 * отдаёт пустой список) — хук не шлёт запрос с чужой роли вовсе.
 * Имя операции отличается от поля slackWorkspaces: иначе кодоген схлопывает
 * тип результата.
 */
export const StudentSlackWorkspacesQuery = gql`
  query StudentSlackWorkspaces($id: ID!) {
    student(id: $id) {
      id
      slackWorkspaces {
        groupId
        groupName
        teamId
        workspaceName
        workspaceUrl
        slackUserId
        displayName
        agreementTerminated
        openWorkspaceUrl
        profileUrl
        appProfileUrl
        adminUrl
      }
    }
  }
`;

// ТЗ 0109: подсказки связей по тексту заголовка. Имя операции отличается от
// поля (`TaskSmartLinks` ≠ `taskSmartLinks`), иначе codegen схлопнет типы.
export const TaskSmartLinksQuery = gql`
  query TaskSmartLinks($text: String!) {
    taskSmartLinks(text: $text) {
      kind
      id
      label
      hint
      reason
      confident
    }
  }
`;

// ТЗ 0109: подписанные inline-ссылки на картинки задач (≤ 50 за вызов).
export const TaskImageUrlsQuery = gql`
  query TaskImageUrls($attachmentIds: [ID!]!) {
    staffTaskImageUrls(attachmentIds: $attachmentIds) {
      id
      url
      expiresAt
    }
  }
`;
